// Ported from Ember: app/utils/time-util.js + app/enums/date-formats.js
// Preserves exact Ember behaviour for display day/time formatting

import type { MenuSchedule, MenuScheduleHour, OrderType } from '@/shared/api/location'
import { ORDER_TYPE_REF } from '@/shared/constants/orderTypes'

const TIME_FORMAT_12_HOUR: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' }
const TIME_FORMAT_24_HOUR: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }

export const DATE_FORMATS: Record<string, Intl.DateTimeFormatOptions> = {
	'en-US': TIME_FORMAT_12_HOUR,
	'en-GB': TIME_FORMAT_24_HOUR,
	'en-IE': TIME_FORMAT_24_HOUR,
	'es-ES': TIME_FORMAT_24_HOUR,
	defaultFormat: TIME_FORMAT_12_HOUR,
}

// Map country codes to locale strings
const COUNTRY_TO_LOCALE: Record<string, string> = {
	US: 'en-US',
	GB: 'en-GB',
	IE: 'en-IE',
	ES: 'es-ES',
}

export function getLocale(countryCode: string): string {
	return COUNTRY_TO_LOCALE[countryCode] ?? 'en-US'
}

// Ember: getAdjustedTime — converts a UTC time to the store's local time
export function getAdjustedTime(timeZoneOffset: number, time: Date = new Date()): Date {
	const browserOffset = time.getTimezoneOffset()
	const storeOffset = timeZoneOffset * -60 || 0
	return new Date(time.getTime() - (storeOffset - browserOffset) * 60000)
}

function isSameDay(a: Date, b: Date): boolean {
	return (
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()
	)
}

// Ember: getDisplayDay — "Today", "Tomorrow", or localised weekday+date
export function getDisplayDay(time: Date, timeZoneOffset: number, countryCode: string): string {
	const timeNow = getAdjustedTime(timeZoneOffset)
	const timeTomorrow = new Date(timeNow.getFullYear(), timeNow.getMonth(), timeNow.getDate() + 1)

	if (isSameDay(time, timeNow)) return 'Today'
	if (isSameDay(time, timeTomorrow)) return 'Tomorrow'

	// time is already store-local (browser-local frame after getAdjustedTime)
	// Read its calendar date in browser-local frame (no timeZone conversion)
	const locale = getLocale(countryCode)
	return time.toLocaleDateString(locale, {
		weekday: 'long',
		month: 'numeric',
		day: 'numeric',
	})
}

// Resolves the store-local Date a slot buckets into for day-label display ("Today"/"Tomorrow"/weekday).
// A slot belongs to a future business day when its applicableDate (store-local midnight) is still
// ahead of now and its own time hasn't passed — then it buckets by its own applicableDate; otherwise
// it buckets against today. The result is adjusted into store-local frame because callers compare it
// against an adjusted "now" via getDisplayDay. Adjusting consistently here is the single source of
// truth that prevents the STO-3128/STO-3863 class of bug, where one call site compared a raw
// applicableDate against an adjusted now. Ember parity: ticket.js:getCustomerDisplayDay.
export function getSlotBucketDate(
	applicableDate: Date,
	expectedTime: Date,
	timeZoneOffset: number,
): Date {
	const now = new Date()
	const isFutureSlot = applicableDate > now && expectedTime > now
	return getAdjustedTime(timeZoneOffset, isFutureSlot ? applicableDate : now)
}

// Display time in customer's local browser timezone
export function getDisplayTime(time: Date, countryCode: string): string {
	const locale = getLocale(countryCode)
	const formatOptions = DATE_FORMATS[locale] ?? DATE_FORMATS.defaultFormat
	return time.toLocaleTimeString(locale, formatOptions)
}

// Formats a plain HH:MM:SS time string (store-local) to a locale time string (e.g. "8:00 AM").
// Uses startTime/endTime from MenuScheduleHour — these are already in the store's local timezone
// and must NOT be parsed as UTC datetimes (that would produce browser-offset-shifted times).
// Returns the raw string on parse failure rather than throwing.
// countryCode drives 12h vs 24h: GB/IE/ES use 24h, all others default to 12h (Ember parity).
export function formatTimeStr(timeStr: string, countryCode: string = 'US'): string {
	const locale = getLocale(countryCode)
	const formatOptions = DATE_FORMATS[locale] ?? DATE_FORMATS.defaultFormat
	// Plain time like "08:00:00" — parse by constructing a date in local time
	const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(timeStr)
	if (match) {
		const hours = parseInt(match[1], 10)
		const minutes = parseInt(match[2], 10)
		const date = new Date(1970, 0, 1, hours, minutes)
		return new Intl.DateTimeFormat(locale, formatOptions).format(date)
	}
	// Fallback: full ISO datetime string — format as UTC to avoid browser offset shifting
	const date = new Date(timeStr)
	if (isNaN(date.getTime())) return timeStr
	return new Intl.DateTimeFormat(locale, { ...formatOptions, timeZone: 'UTC' }).format(date)
}

// STO-3195 — Overnight schedule day suffix.
// Ember parity (order-type-modal.js:280):
//   displayDaysName = (getDisplayDay(expectedTime) !== getCustomerDisplayDay(applicableDate, expectedTime))
//     ? ` (${daysOfWeek[getDay(expectedTime)]})` : ''
//
// A slot gets a suffix when its store-local calendar day > applicableDate's calendar day.
// This handles: pre-midnight slots (same day as applicableDate, no suffix) vs post-midnight
// slots (next calendar day, show suffix). The displayDay comparison naturally captures this.
const SHORT_DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function getOvernightDayLabel(
	applicableDate: Date,
	expectedTime: Date,
	timeZoneOffset: number,
	countryCode: string = 'US',
): string {
	const storeExpected = getAdjustedTime(timeZoneOffset, expectedTime)
	const storeTimeForBucket = getSlotBucketDate(applicableDate, expectedTime, timeZoneOffset)

	// Compare display days: if they match, no suffix
	const expectedDisplayDay = getDisplayDay(storeExpected, timeZoneOffset, countryCode)
	const bucketDisplayDay = getDisplayDay(storeTimeForBucket, timeZoneOffset, countryCode)

	if (expectedDisplayDay === bucketDisplayDay) return ''
	return ` (${SHORT_DAYS_OF_WEEK[storeExpected.getDay()]})`
}

// STO-3862 — Overnight schedule end-day suffix for the menu schedule pill/dropdown.
// Ember parity (menu-schedule-hour.js:prettyDate): when a schedule hour's window crosses midnight,
// its store-local end weekday differs from its start weekday, so append " (Wed)" using the END
// weekday abbreviation. Each instant is shifted by the store's fixed offset and read with getUTCDay(),
// so the weekday depends only on the store offset — independent of the viewer's browser timezone/DST.
// (A browser-local getDay() via getAdjustedTime can flip the weekday near the browser's own DST
// transition when the store is in a different timezone.) Returns '' for same-day windows or bad input.
export function getScheduleEndDayLabel(
	startDateTime: string,
	endDateTime: string,
	timeZoneOffset: number,
): string {
	const start = new Date(startDateTime)
	const end = new Date(endDateTime)
	if (isNaN(start.getTime()) || isNaN(end.getTime())) return ''
	const storeStartDay = new Date(start.getTime() + timeZoneOffset * 3_600_000).getUTCDay()
	const storeEndDay = new Date(end.getTime() + timeZoneOffset * 3_600_000).getUTCDay()
	if (storeStartDay === storeEndDay) return ''
	return ` (${SHORT_DAYS_OF_WEEK[storeEndDay]})`
}

// Returns the hour entry best suited for display in the schedule dropdown.
// Prefers the currently active window; falls back to the next upcoming (soonest startDateTime);
// then the first entry for schedules whose entire week is in the past.
// Returns null only when hours is empty.
export function getScheduleHourForDisplay(schedule: MenuSchedule): MenuScheduleHour | null {
	if (schedule.hours.length === 0) return null
	const now = new Date()
	const current = schedule.hours.find(
		(h) => now >= new Date(h.startDateTime) && now < new Date(h.endDateTime),
	)
	if (current) return current
	const upcoming = schedule.hours
		.filter((h) => new Date(h.startDateTime) > now)
		.sort(
			(a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime(),
		)[0]
	return upcoming ?? schedule.hours[0]
}

// Priority - DSP: Math.max(distanceLeadTimeInMinutes, storeDeliveryLeadTimeInMinutes).
// Non-DSP DELIVERY: storeDeliveryLeadTimeInMinutes - > 0 guard matches Ember's non-zero check.
// All other order types (TAKEOUT, DINE_IN): pickupLeadTimeInMinutes.
export function getAsapLeadTimeMinutes(
	orderType: OrderType,
	distanceLeadTimeInMinutes: number | null,
): number | null {
	if (orderType.deliveryServedWithDSP && distanceLeadTimeInMinutes != null) {
		return Math.max(distanceLeadTimeInMinutes, orderType.storeDeliveryLeadTimeInMinutes) || null
		// "0" result means no meaningful lead time, so fall back to "Ready soon"
	}
	if (orderType.ref === ORDER_TYPE_REF.DELIVERY) {
		return orderType.storeDeliveryLeadTimeInMinutes > 0
			? orderType.storeDeliveryLeadTimeInMinutes
			: null
	}
	return orderType.pickupLeadTimeInMinutes
}

// Ported from Ember: app/utils/time-util.js — convertMinutesToHoursAndMinutes
// Used by EtaTime component (app/components/eta-time.js) to render the ASAP subtitle
// e.g. 20 → "20 minutes", 90 → "1 hour and 30 minutes", 60 → "1 hour"
export function convertMinutesToHoursAndMinutes(minutes: number): string {
	if (isNaN(minutes) || minutes < 0) return 'Invalid input'

	const hours = Math.floor(minutes / 60)
	const remainingMinutes = minutes % 60

	if (hours === 0) return `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`
	if (remainingMinutes === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`
	return `${hours} hour${hours !== 1 ? 's' : ''} and ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`
}
